import { GeneralDetailsFormData } from '../../components/offers/general/form/details/general';
import { CustomerDetailsFormData } from '../../components/offers/general/form/details/customer';
import { FreightDetailsFormData } from '../../components/offers/general/form/details/freight';
import { AddressDetailsFormData } from '../../components/offers/general/form/details/address';
import GeneralService from '../general';

export interface ISavedOfferHistoryData {
    id?: string;
    savedAt: string;
    orderDetails?: GeneralDetailsFormData;
    customerDetails?: CustomerDetailsFormData;
    freightDetails?: FreightDetailsFormData;
    addressDetails?: AddressDetailsFormData;
}

const STORAGE_KEY = 'jg_last_offer_step_history';
const HISTORY_LIST_KEY = 'jg_offer_step_history_list';
const MAX_HISTORY_ITEMS = 10;

const OfferStorageService = {
    /**
     * Get the full list of saved offer history records (sorted newest first)
     */
    getList: (): ISavedOfferHistoryData[] => {
        try {
            const raw = localStorage.getItem(HISTORY_LIST_KEY);
            if (!raw) {
                // Backward compatibility: migrate from old single item if exists
                const oldSingle = OfferStorageService.get();
                if (oldSingle && (oldSingle.orderDetails?.subsidiary || oldSingle.customerDetails?.company)) {
                    const migrated = [{ ...oldSingle, id: oldSingle.id || GeneralService.guid() }];
                    localStorage.setItem(HISTORY_LIST_KEY, JSON.stringify(migrated));
                    return migrated;
                }
                return [];
            }
            const parsed = JSON.parse(raw) as ISavedOfferHistoryData[];
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error('Failed to parse offer history list from localStorage', e);
            return [];
        }
    },

    /**
     * Helper to push or update an item in the history list
     */
    saveToList: (entry: ISavedOfferHistoryData) => {
        try {
            const list = OfferStorageService.getList();
            const companyId = entry.customerDetails?.company?.id;
            const customerId = entry.customerDetails?.customer?.id;

            // Check if there is an existing entry with the same Company & Customer
            const existingIndex = list.findIndex(item => {
                const itemCompanyId = item.customerDetails?.company?.id;
                const itemCustomerId = item.customerDetails?.customer?.id;
                if (companyId && itemCompanyId && companyId === itemCompanyId) {
                    return !customerId || !itemCustomerId || customerId === itemCustomerId;
                }
                return false;
            });

            const updatedEntry: ISavedOfferHistoryData = {
                ...entry,
                id: entry.id || (existingIndex > -1 ? list[existingIndex].id : GeneralService.guid()),
                savedAt: new Date().toISOString()
            };

            if (existingIndex > -1) {
                // Merge and move to front
                const existing = list[existingIndex];
                list.splice(existingIndex, 1);
                list.unshift({
                    ...existing,
                    ...updatedEntry,
                    orderDetails: updatedEntry.orderDetails || existing.orderDetails,
                    customerDetails: updatedEntry.customerDetails || existing.customerDetails,
                    freightDetails: updatedEntry.freightDetails || existing.freightDetails,
                    addressDetails: updatedEntry.addressDetails || existing.addressDetails,
                });
            } else {
                list.unshift(updatedEntry);
            }

            // Limit to MAX_HISTORY_ITEMS
            const trimmed = list.slice(0, MAX_HISTORY_ITEMS);
            localStorage.setItem(HISTORY_LIST_KEY, JSON.stringify(trimmed));
            // Keep single key synced with latest
            localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed[0]));
        } catch (e) {
            console.error('Failed to save to offer history list', e);
        }
    },

    saveStep1: (orderDetails?: GeneralDetailsFormData, customerDetails?: CustomerDetailsFormData) => {
        try {
            const existing = OfferStorageService.get() || { savedAt: new Date().toISOString() };
            const updated: ISavedOfferHistoryData = {
                ...existing,
                savedAt: new Date().toISOString(),
                orderDetails: orderDetails || existing.orderDetails,
                customerDetails: customerDetails || existing.customerDetails,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

            // Only add to history list if at least company or customer has been selected
            if (customerDetails?.company || customerDetails?.customer) {
                OfferStorageService.saveToList(updated);
            }
        } catch (e) {
            console.error('Failed to save offer step 1 to localStorage', e);
        }
    },

    saveStep2: (freightDetails?: FreightDetailsFormData, addressDetails?: AddressDetailsFormData) => {
        try {
            const existing = OfferStorageService.get() || { savedAt: new Date().toISOString() };
            const updated: ISavedOfferHistoryData = {
                ...existing,
                savedAt: new Date().toISOString(),
                freightDetails: freightDetails || existing.freightDetails,
                addressDetails: addressDetails || existing.addressDetails,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

            if (updated.customerDetails?.company || updated.customerDetails?.customer) {
                OfferStorageService.saveToList(updated);
            }
        } catch (e) {
            console.error('Failed to save offer step 2 to localStorage', e);
        }
    },

    saveAll: (
        orderDetails?: GeneralDetailsFormData,
        customerDetails?: CustomerDetailsFormData,
        freightDetails?: FreightDetailsFormData,
        addressDetails?: AddressDetailsFormData
    ) => {
        try {
            const data: ISavedOfferHistoryData = {
                id: GeneralService.guid(),
                savedAt: new Date().toISOString(),
                orderDetails,
                customerDetails,
                freightDetails,
                addressDetails,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            if (customerDetails?.company || customerDetails?.customer) {
                OfferStorageService.saveToList(data);
            }
        } catch (e) {
            console.error('Failed to save offer data to localStorage', e);
        }
    },

    get: (): ISavedOfferHistoryData | null => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) {
                const list = OfferStorageService.getList();
                return list.length > 0 ? list[0] : null;
            }
            const parsed = JSON.parse(raw) as ISavedOfferHistoryData;
            return parsed;
        } catch (e) {
            console.error('Failed to parse offer history from localStorage', e);
            return null;
        }
    },

    hasData: (): boolean => {
        const list = OfferStorageService.getList();
        if (list.length > 0) return true;
        const data = OfferStorageService.get();
        if (!data) return false;
        const hasStep1 = !!(data.orderDetails?.subsidiary || data.customerDetails?.company);
        const hasStep2 = !!(data.freightDetails?.freightCategory || (data.addressDetails?.originAddresses && data.addressDetails.originAddresses.length > 0));
        return hasStep1 || hasStep2;
    },

    remove: (id: string) => {
        try {
            const list = OfferStorageService.getList().filter(item => item.id !== id);
            localStorage.setItem(HISTORY_LIST_KEY, JSON.stringify(list));
            if (list.length > 0) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(list[0]));
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch (e) {
            console.error('Failed to remove offer history item from localStorage', e);
        }
    },

    clear: () => {
        try {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(HISTORY_LIST_KEY);
        } catch (e) {
            console.error('Failed to clear offer history from localStorage', e);
        }
    },

    /**
     * Clones address details and assigns fresh GUIDs so that they are treated
     * as new records rather than colliding with previously submitted records.
     */
    prepareClonedAddressDetails: (addressDetails?: AddressDetailsFormData): AddressDetailsFormData | undefined => {
        if (!addressDetails) return undefined;
        return {
            originAddresses: (addressDetails.originAddresses || []).map((addr, idx) => ({
                ...addr,
                id: GeneralService.guid(),
                orderNumber: idx + 1,
            })),
            destinationAddresses: (addressDetails.destinationAddresses || []).map((addr, idx) => ({
                ...addr,
                id: GeneralService.guid(),
                orderNumber: idx + 1,
            })),
        };
    }
};

export default OfferStorageService;
